import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createRoot, Root } from 'react-dom/client';

interface SelectionInfo {
  start: number;
  end: number;
}

interface TextSelectionFieldProps {
  label: string;
  type: 'input' | 'textarea';
  value: string;
  onChange: (value: string) => void;
  selectionInfo: SelectionInfo;
  onSelectionChange: (info: SelectionInfo) => void;
  onWrapSelection: () => void;
}

const TextSelectionField: React.FC<TextSelectionFieldProps> = ({
  label,
  type,
  value,
  onChange,
  selectionInfo,
  onSelectionChange,
  onWrapSelection,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Define handleSelection with useCallback to make it stable
  const handleSelection = useCallback(() => {
    let start = 0;
    let end = 0;

    if (type === 'input' && inputRef.current) {
      start = inputRef.current.selectionStart || 0;
      end = inputRef.current.selectionEnd || 0;
    } else if (type === 'textarea' && textareaRef.current) {
      start = textareaRef.current.selectionStart || 0;
      end = textareaRef.current.selectionEnd || 0;
    }

    onSelectionChange({ start, end });
  }, [type, onSelectionChange]);

  const wrapSelectionWithBold = () => {
    if (selectionInfo.start === selectionInfo.end) {
      return; // No selection
    }

    if (type === 'input' && inputRef.current) {
      const selectedText = value.substring(selectionInfo.start, selectionInfo.end);
      const newValue =
        value.substring(0, selectionInfo.start) +
        `**${selectedText}**` +
        value.substring(selectionInfo.end);
      onChange(newValue);
      
      // Set cursor position after the inserted text
      setTimeout(() => {
        const newPosition = selectionInfo.start + selectedText.length + 4; // +4 for **
        inputRef.current?.setSelectionRange(newPosition, newPosition);
      }, 0);
    } else if (type === 'textarea' && textareaRef.current) {
      const selectedText = value.substring(selectionInfo.start, selectionInfo.end);
      const newValue =
        value.substring(0, selectionInfo.start) +
        `**${selectedText}**` +
        value.substring(selectionInfo.end);
      onChange(newValue);
      
      // Set cursor position after the inserted text
      setTimeout(() => {
        const newPosition = selectionInfo.start + selectedText.length + 4; // +4 for **
        textareaRef.current?.setSelectionRange(newPosition, newPosition);
      }, 0);
    }
  };

  return (
    <div className="mb-6 p-4 border border-gray-300 rounded-lg">
      <div className="mb-2">
        <span className="text-sm font-medium">
          select text index - start:{selectionInfo.start} end:{selectionInfo.end}
        </span>
        <button
          onClick={wrapSelectionWithBold}
          className="ml-4 px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          B
        </button>
      </div>
      
      {type === 'input' && (
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onSelect={handleSelection}
          onMouseUp={handleSelection}
          onKeyUp={handleSelection}
          className="w-full p-2 border border-gray-400 rounded"
        />
      )}
      
      {type === 'textarea' && (
        <div 
          style={{ 
            position: 'relative',
            backgroundColor: 'transparent',
            border: '1px solid transparent',
            borderRadius: '0px',
            transition: 'all 0.2s ease',
          }}
          className="textarea-wrapper"
        >
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onSelect={handleSelection}
            onMouseUp={handleSelection}
            onKeyUp={handleSelection}
            placeholder={value === '' ? 'Type a message' : ''}
            dir="auto"
            translate="no"
            data-placeholder={value === '' ? 'Type a message' : ''}
            style={{
              minHeight: '85px',
              maxHeight: '200px',
              fontSize: '16px',
              lineHeight: '1.5',
              fontFamily: 'inherit',
              color: 'var(--color-primary, #000)',
              backgroundColor: 'transparent',
              border: 'none',
              outline: 'none',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
              overflow: 'auto',
              cursor: 'text',
              scrollbarGutter: 'stable',
              padding: '16px',
              width: '100%',
              resize: 'none',
              boxSizing: 'border-box',
            }}
            className="message-textarea"
          />
          <style>{`
            .textarea-wrapper:focus-within {
              border-color: #51b3e2;
              box-shadow: 0 0 0 3px rgba(81, 179, 226, 0.1);
            }
            .message-textarea::placeholder {
              color: #999;
              opacity: 1;
            }
            .message-textarea:focus {
              outline: none;
              box-shadow: none;
              border: none;
            }
            .message-textarea:focus-visible {
              outline: none;
              box-shadow: none;
              border: none;
            }
            [data-placeholder]:empty:before {
              content: attr(data-placeholder);
              color: #999;
              pointer-events: none;
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

const ShadowDOMTextSelection: React.FC = () => {
  const [inputValue, setInputValue] = useState('Sample text for input field');
  const [textareaValue, setTextareaValue] = useState('Sample text for textarea field');
  const [editableDivValue, setEditableDivValue] = useState('Sample text for editable div (now textarea)');
  
  const [inputSelection, setInputSelection] = useState<SelectionInfo>({ start: 0, end: 0 });
  const [textareaSelection, setTextareaSelection] = useState<SelectionInfo>({ start: 0, end: 0 });
  const [editableDivSelection, setEditableDivSelection] = useState<SelectionInfo>({ start: 0, end: 0 });

  return (
    <div className="p-6 bg-white">
      <h2 className="text-2xl font-bold mb-6">Shadow DOM Text Selection POC</h2>
      
      <TextSelectionField
        label="Input Text"
        type="input"
        value={inputValue}
        onChange={setInputValue}
        selectionInfo={inputSelection}
        onSelectionChange={setInputSelection}
        onWrapSelection={() => {}}
      />
      
      <TextSelectionField
        label="Textarea"
        type="textarea"
        value={textareaValue}
        onChange={setTextareaValue}
        selectionInfo={textareaSelection}
        onSelectionChange={setTextareaSelection}
        onWrapSelection={() => {}}
      />
      
      <TextSelectionField
        label="Editable Div (Replaced with Textarea)"
        type="textarea"
        value={editableDivValue}
        onChange={setEditableDivValue}
        selectionInfo={editableDivSelection}
        onSelectionChange={setEditableDivSelection}
        onWrapSelection={() => {}}
      />
    </div>
  );
};

export const injectShadowDOMTextSelection = async (container: HTMLElement): Promise<void> => {
  // Create shadow root
  const shadowRoot = container.attachShadow({ mode: 'open' });
  
  // Create wrapper div
  const wrapper = document.createElement('div');
  wrapper.className = 'btcss';
  shadowRoot.appendChild(wrapper);
  
  // Inject Tailwind styles into shadow root
  const { injectTailwindIntoShadowRoot } = await import('@extension/utils/tailwindStyles');
  await injectTailwindIntoShadowRoot(shadowRoot, { includeWrapper: false });
  
  // Create React root and render component
  const root: Root = createRoot(wrapper);
  root.render(<ShadowDOMTextSelection />);
  
  // Store root reference for cleanup if needed
  (container as any).__shadowRoot = shadowRoot;
  (container as any).__reactRoot = root;
};

export default ShadowDOMTextSelection;

